import type { ContextGuardDismissibleBand } from './context.guard.types';

// An explicit X means "do not show this band for this thread again", so the choice is durable across
// reloads and browser sessions. Each stronger band remains independent and may still appear later.
const CONTEXT_GUARD_DISMISSED_KEY = 'agent-context-guard-dismissed';

/**
 * Dismissal key for one notice. Keyed by thread AND band so the two nudges are independent:
 * dismissing the gentle `advisory` must not also silence the firmer `approaching` notice
 * once the same chat grows further.
 */
export function contextGuardDismissalKey(threadId: string | null, band: ContextGuardDismissibleBand): string {

  return `${threadId ?? 'new'}:${band}`;
}

/** Keys the user has dismissed. Storage failures degrade to "nothing dismissed". */
export function loadDismissedContextGuards(): Set<string> {

  try {
    const raw = localStorage.getItem(CONTEXT_GUARD_DISMISSED_KEY);
    const keys = raw ? JSON.parse(raw) as unknown : [];
    return new Set(Array.isArray(keys) ? keys.filter((key): key is string => typeof key === 'string') : []);
  } catch {
    return new Set();
  }
}

/** Persist one dismissal, returning the updated set so callers can drive a signal from the result. */
export function markContextGuardDismissed(key: string): Set<string> {

  const dismissed = loadDismissedContextGuards();
  dismissed.add(key);
  try {
    localStorage.setItem(CONTEXT_GUARD_DISMISSED_KEY, JSON.stringify([...dismissed]));
  } catch {
    // Non-fatal: worst case the notice reappears on the next reload.
  }
  return dismissed;
}
